import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { BlogArticleObj, SeoLink, SeoSiblingsBlock } from '~/types/school'
import type {
  BlogSearchArticleMockRow,
  BlogSearchCategoryTab,
  BlogSearchSeoPayload,
  UseBlogSearchHubReturn,
} from '~/types/BlogSearchHub'
import { parseRouteQueryString } from '~/utils/establishmentSearchUrl'

/**
 * Parse un bloc JSON SEO (parent, enfants, sœurs…).
 * @param {string | undefined} json - Chaîne JSON ou vide.
 * @returns {SeoSiblingsBlock} Objet parsé ou bloc vide si invalide.
 */
function parseBlock(json: string | undefined): SeoSiblingsBlock {
  if (!json) return { Title: '', Links: [] }
  try {
    return JSON.parse(json) as SeoSiblingsBlock
  } catch {
    return { Title: '', Links: [] }
  }
}

/**
 * Extrait le slug catégorie depuis une URL type `/nos-conseils/vie-etudiante`.
 * @param {string} url - URL absolue ou relative (ex. lien issu de `childrenBlock`).
 * @returns {string | null} Segment après `/nos-conseils/`, sinon `null`.
 */
function slugFromNosConseilsUrl(url: string): string | null {
  const m: RegExpMatchArray | null = url.trim().match(/\/nos-conseils\/([^/?#]+)/i)
  return m?.[1] ?? null
}

/** Si l'API ne renvoie pas `childrenBlock`, on garde les catégories alignées sur l'ancien site. */
const FALLBACK_CATEGORY_LINKS: SeoLink[] = [
  { Url: '/nos-conseils/examens-oraux', Text: 'Examens' },
  { Url: '/nos-conseils/orientation-etudes', Text: 'Orientation' },
  { Url: '/nos-conseils/vie-etudiante', Text: 'Vie étudiante' },
  { Url: '/nos-conseils/1er-emploi', Text: '1er emploi' },
]

/**
 * Données mock : liste d'articles pour la page recherche blog.
 * Aligné sur les catégories renvoyées par `seointer` (childrenBlock).
 * @returns {BlogSearchArticleMockRow[]} Articles avec slug de catégorie et mots-clés pour le filtre texte.
 */
export function getMockBlogSearchArticles(): BlogSearchArticleMockRow[] {
  const rows: BlogSearchArticleMockRow[] = [
    {
      id: 1,
      Title: "Comment choisir sa ville d'études ?",
      Description:
        'Critères pour choisir la ville idéale : qualité de vie, budget, transports et vie étudiante.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Vie étudiante',
      categorySlug: 'vie-etudiante',
      ReadingTime: 5,
      Link: '/nos-conseils/vie-etudiante/choisir-ville-etudes',
      keywords: 'ville études logement budget transport',
    },
    {
      id: 2,
      Title: 'Top 10 des villes étudiantes en France',
      Description:
        'Classement des villes où il fait bon étudier : formations, vie associative, coût.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Vie étudiante',
      categorySlug: 'vie-etudiante',
      ReadingTime: 6,
      Link: '/nos-conseils/vie-etudiante/top-villes-etudiantes',
      keywords: 'classement villes france étudiant',
    },
    {
      id: 3,
      Title: 'Réussir son oral de bac : méthode et confiance',
      Description: 'Préparer son grand oral : structure, gestion du stress et exemples concrets.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Examens',
      categorySlug: 'examens-oraux',
      ReadingTime: 8,
      Link: '/nos-conseils/examens-oraux/reussir-oral-bac',
      keywords: 'bac oral grand oral stress méthode',
    },
    {
      id: 4,
      Title: 'Parcoursup : bien classer ses vœux',
      Description:
        'Stratégie pour formuler ses choix et éviter les erreurs fréquentes sur Parcoursup.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Orientation',
      categorySlug: 'orientation-etudes',
      ReadingTime: 7,
      Link: '/nos-conseils/orientation-etudes/parcoursup-voeux',
      keywords: 'parcoursup vœux orientation stratégie',
    },
    {
      id: 5,
      Title: "Étudier à l'étranger : les meilleures destinations",
      Description: 'Comparer les destinations selon ton budget, la langue et les débouchés.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Orientation',
      categorySlug: 'orientation-etudes',
      ReadingTime: 7,
      Link: '/nos-conseils/etudes-a-letranger/etudier-etranger',
      keywords: 'étranger erasmus destination langue',
    },
    {
      id: 6,
      Title: 'Premier CV sans expérience : le modèle qui fonctionne',
      Description: 'Mettre en avant stages, bénévolat et projets pour convaincre un recruteur.',
      Image: '/images/emptyCoverPicture.png',
      Category: '1er emploi',
      categorySlug: '1er-emploi',
      ReadingTime: 5,
      Link: '/nos-conseils/1er-emploi/cv-sans-experience',
      keywords: 'cv stage recrutement premier job',
    },
    {
      id: 7,
      Title: "Entretien d'embauche : les questions classiques",
      Description:
        'Anticiper les questions et préparer des réponses percutantes, même en alternance.',
      Image: '/images/emptyCoverPicture.png',
      Category: '1er emploi',
      categorySlug: '1er-emploi',
      ReadingTime: 6,
      Link: '/nos-conseils/1er-emploi/entretien-embauche',
      keywords: 'entretien embauche alternance questions',
    },
    {
      id: 8,
      Title: 'Organiser ses révisions avant les partiels',
      Description:
        'Planning, méthode Pomodoro et sommeil : les bases pour des révisions efficaces.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Examens',
      categorySlug: 'examens-oraux',
      ReadingTime: 5,
      Link: '/nos-conseils/examens-oraux/revisions-partiels',
      keywords: 'révisions partiels planning sommeil',
    },
    {
      id: 9,
      Title: "Associations étudiantes : comment s'investir ?",
      Description:
        'Trouver une asso alignée avec tes valeurs et développer des compétences utiles.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Vie étudiante',
      categorySlug: 'vie-etudiante',
      ReadingTime: 4,
      Link: '/nos-conseils/vie-etudiante/associations-etudiantes',
      keywords: 'association campus engagement bénévolat',
    },
  ]
  return rows
}

/**
 * État et logique pour la page recherche articles (`/recherche/nos-conseils`) : SEO via `GET /api/aca/seointer`,
 * onglets catégories (hub), liste d'articles mock filtrée par `cat` et texte.
 * @returns {UseBlogSearchHubReturn} Réactifs (`Ref` / `ComputedRef`) et fonction `filterArticles`.
 */
export function useBlogSearchHub(): UseBlogSearchHubReturn {
  const route = useRoute()
  const { locale } = useI18n()

  const categorySlug: ComputedRef<string | null> = computed((): string | null => {
    const s: string = parseRouteQueryString(route.query.cat)
    if (s === '' || s === 'tout') return null
    return s
  })

  const seoPayload: Ref<BlogSearchSeoPayload | null> = ref<BlogSearchSeoPayload | null>(null)
  const hubChildren: Ref<SeoSiblingsBlock> = ref<SeoSiblingsBlock>({ Title: '', Links: [] })
  const hubNavLoaded: Ref<boolean> = ref<boolean>(false)
  const loading: Ref<boolean> = ref<boolean>(false)
  const error: Ref<string | null> = ref<string | null>(null)

  const basePath: string = '/recherche/nos-conseils'

  /**
   * Charge ou recharge le SEO depuis FrontApi : d'abord le hub (liens catégories dans `childrenBlock`),
   * puis la variante avec `param1` si une catégorie est active. Met à jour `loading`, `error`, `seoPayload` et `hubChildren`.
   * @returns {Promise<void>} Promesse résolue à la fin de la requête (succès ou erreur).
   */
  async function fetchSeo(): Promise<void> {
    loading.value = true
    error.value = null
    const lang: string = locale.value
    const p1: string | null = categorySlug.value
    const api = useFrontApi()

    try {
      if (!hubNavLoaded.value) {
        const { data: hubData } = await api.get<BlogSearchSeoPayload>('/api/aca/seointer', {
          params: { language: lang, pageType: 'blog-articles' },
        })
        hubChildren.value = parseBlock(hubData.childrenBlock)
        hubNavLoaded.value = true
        if (!p1) {
          seoPayload.value = hubData
          return
        }
      }

      const { data } = await api.get<BlogSearchSeoPayload>('/api/aca/seointer', {
        params: { language: lang, pageType: 'blog-articles', ...(p1 ? { param1: p1 } : {}) },
      })
      seoPayload.value = data
    } catch (e: unknown) {
      const err: Error = e instanceof Error ? e : new Error(String(e))
      error.value = err.message ?? 'Erreur réseau'
      seoPayload.value = null
      if (!hubNavLoaded.value) {
        hubChildren.value = { Title: '', Links: [] }
      }
    } finally {
      loading.value = false
    }
  }

  watch(locale, (): void => {
    hubNavLoaded.value = false
  })

  watch(
    (): readonly [string, string | null] => [locale.value, categorySlug.value],
    (): void => {
      void fetchSeo()
    },
    { immediate: true },
  )

  const categoryTabs: ComputedRef<BlogSearchCategoryTab[]> = computed(
    (): BlogSearchCategoryTab[] => {
      const tabs: BlogSearchCategoryTab[] = [{ slug: null, label: '', path: basePath, query: {} }]
      const rawLinks: SeoLink[] = hubChildren.value.Links ?? []
      const links: SeoLink[] = rawLinks.length > 0 ? rawLinks : FALLBACK_CATEGORY_LINKS
      for (const sl of links) {
        const slug: string | null = slugFromNosConseilsUrl(sl.Url)
        if (!slug) continue
        tabs.push({
          slug,
          label: sl.Text ?? '',
          path: basePath,
          query: { cat: slug },
        })
      }
      return tabs
    },
  )

  const allArticles: ComputedRef<BlogSearchArticleMockRow[]> = computed(
    (): BlogSearchArticleMockRow[] => getMockBlogSearchArticles(),
  )

  function filterArticles(searchQuery: string): BlogArticleObj[] {
    const q: string = searchQuery.trim().toLowerCase()
    const cat: string | null = categorySlug.value
    let list: BlogSearchArticleMockRow[] = allArticles.value.filter(
      (a: BlogSearchArticleMockRow) => (cat ? a.categorySlug === cat : true),
    )
    if (q.length > 0) {
      list = list.filter(
        (a: BlogSearchArticleMockRow) =>
          a.Title.toLowerCase().includes(q) ||
          a.Description.toLowerCase().includes(q) ||
          a.keywords.includes(q),
      )
    }
    return list.map(
      ({
        categorySlug: _categorySlug,
        keywords: _keywords,
        ...rest
      }: BlogSearchArticleMockRow): BlogArticleObj => rest,
    )
  }

  return {
    seoPayload,
    hubChildren,
    categoryTabs,
    categorySlug,
    basePath,
    loading,
    error,
    filterArticles,
  }
}
